import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShieldCheck, Wrench, Euro, Plus, Clock } from "lucide-react";
import { api } from "../lib/api";
import { eur, fmtDate, clienteName } from "../lib/format";
import { PageHeader } from "../components/Layout";
import { Targa } from "../components/Targa";
import { StatoBadge, TipoBadge } from "../components/Badges";
import { Button } from "../components/ui/button";

const Kpi = ({ label, value, sub, icon: Icon, tone, testId, delay }) => (
  <div className="rise rounded-md border bg-white p-5" style={{ animationDelay: `${delay}ms` }}>
    <div className="flex items-start justify-between">
      <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <Icon className={`h-5 w-5 ${tone}`} />
    </div>
    <div className="mt-3 font-display text-4xl font-semibold tracking-tight" data-testid={testId}>{value}</div>
    {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

const GaranziaRow = ({ g, prefix }) => (
  <Link to={`/garanzie/${g.id}`} data-testid={`${prefix}-${g.id}`}
    className="flex flex-wrap items-center gap-3 border-b px-5 py-3 transition-colors last:border-0 hover:bg-slate-50">
    <Targa value={g.veicolo?.targa} />
    <div className="min-w-0 flex-1">
      <div className="truncate text-sm font-semibold">{clienteName(g.cliente)}</div>
      <div className="truncate text-xs text-muted-foreground">{g.veicolo?.marca} {g.veicolo?.modello} · <span className="font-mono">{g.numero}</span></div>
    </div>
    <TipoBadge g={g} />
    <div className="w-28 text-right text-xs">
      <div className="font-semibold">{fmtDate(g.data_fine)}</div>
      <div className={g.giorni_rimanenti < 0 ? "text-red-600" : "text-amber-600"}>
        {g.giorni_rimanenti < 0 ? `scaduta da ${-g.giorni_rimanenti} gg` : `tra ${g.giorni_rimanenti} gg`}
      </div>
    </div>
  </Link>
);

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="text-sm text-muted-foreground">Caricamento…</div>;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle={`${d.clienti} clienti · ${d.veicoli} veicoli · ${d.totale} garanzie registrate`}>
        <Button asChild className="bg-[#B08D57] hover:bg-[#96764A]" data-testid="dashboard-new-garanzia-btn">
          <Link to="/garanzie?nuova=1"><Plus className="mr-1 h-4 w-4" /> Nuova garanzia</Link>
        </Button>
      </PageHeader>

      {d.in_scadenza > 0 && (
        <div className="rise mb-6 flex items-center gap-3 rounded-md border border-amber-300 bg-amber-50 px-5 py-4 text-amber-900" data-testid="expiring-alert">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <div className="text-sm"><b>{d.in_scadenza} garanzie</b> scadono nei prossimi 30 giorni. Contatta i clienti per il rinnovo o un'estensione commerciale.</div>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi label="Garanzie attive" value={d.attive} sub={`${d.conformita_attive} conformità · ${d.commerciali_attive} commerciali`} icon={ShieldCheck} tone="text-emerald-600" testId="kpi-attive" delay={0} />
        <Kpi label="In scadenza (30gg)" value={d.in_scadenza} sub={`${d.scadute} scadute in totale`} icon={Clock} tone="text-amber-500" testId="kpi-in-scadenza" delay={60} />
        <Kpi label="Reclami aperti" value={d.reclami_aperti} sub={`Costo interventi ${eur(d.costo_interventi)}`} icon={Wrench} tone="text-blue-600" testId="kpi-reclami" delay={120} />
        <Kpi label="Ricavi commerciali" value={eur(d.ricavi_commerciali)} sub="Garanzie commerciali vendute" icon={Euro} tone="text-[#B08D57]" testId="kpi-ricavi" delay={180} />
      </div>

      <div className="mt-8 grid gap-6 xl:grid-cols-[1.4fr_1fr]">
        <section className="rise rounded-md border bg-white" data-testid="expiring-list">
          <div className="flex items-center justify-between border-b px-5 py-4">
            <h2 className="font-display text-lg font-bold">Garanzie in scadenza</h2>
            <Link to="/garanzie?stato=in_scadenza" className="text-xs font-semibold text-[#B08D57] hover:underline">Vedi tutte</Link>
          </div>
          {d.lista_in_scadenza.length === 0 && d.scadute_recenti.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessuna garanzia in scadenza nei prossimi 30 giorni.</div>
          ) : (
            <>
              {d.lista_in_scadenza.map((g) => <GaranziaRow key={g.id} g={g} prefix="expiring-row" />)}
              {d.scadute_recenti.length > 0 && (
                <div className="border-t bg-slate-50 px-5 py-2 text-[11px] font-semibold uppercase tracking-wider text-red-600">Scadute negli ultimi 30 giorni</div>
              )}
              {d.scadute_recenti.map((g) => <GaranziaRow key={g.id} g={g} prefix="expired-row" />)}
            </>
          )}
        </section>

        <section className="rise rounded-md border bg-white">
          <div className="border-b px-5 py-4"><h2 className="font-display text-lg font-bold">Ultime attivazioni</h2></div>
          {d.recenti.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessuna garanzia ancora. Inizia registrando un cliente e un veicolo.</div>
          ) : d.recenti.map((g) => (
            <Link key={g.id} to={`/garanzie/${g.id}`} className="flex items-center gap-3 border-b px-5 py-3 last:border-0 hover:bg-slate-50" data-testid={`recent-row-${g.id}`}>
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-semibold">{clienteName(g.cliente)}</div>
                <div className="font-mono text-xs text-muted-foreground">{g.numero}</div>
              </div>
              <TipoBadge g={g} />
              <StatoBadge stato={g.stato} />
            </Link>
          ))}
        </section>
      </div>
    </div>
  );
}
